import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

export default function Reports() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/reports/stages?projectName=${encodeURIComponent(projectName)}`)
      .then(res => {
        const stageList = res.data.filter(s => s !== 'כל השלבים');
        setStages(stageList);
        setSelectedStages(stageList);
      })
      .catch(() => {});
  }, [projectName]);

  useEffect(() => {
    fetchStats();
  }, [selectedStages, projectName]);

  function getStageParam() {
    if (selectedStages.length === 0 || selectedStages.length === stages.length) return 'כל השלבים';
    return selectedStages.join(',');
  }

  async function fetchStats() {
    setLoading(true);
    try {
      const res = await api.get(`/reports/stats?projectName=${encodeURIComponent(projectName)}&stage=${encodeURIComponent(getStageParam())}`);
      setRows(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  function toggleStage(stage) {
    setSelectedStages(prev => {
      const next = prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage];
      return next.length === 0 ? [...stages] : next;
    });
  }

  async function handleExport(path, filename) {
    const isCSV = filename.endsWith('.csv');
    const isPPTX = filename.endsWith('.pptx');
    const res = await api.get(
      `/reports/${path}?projectName=${encodeURIComponent(projectName)}&stage=${encodeURIComponent(getStageParam())}`,
      { responseType: 'blob' }
    );
    let mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    if (isCSV) mimeType = 'text/csv;charset=utf-8;';
    if (isPPTX) mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  return (
    <div>
      <Navbar title={`דוחות — ${decodeURIComponent(projectName)}`} breadcrumb="פרויקטים > דוחות" />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 className="page-title">דוחות אחוזי חתימות</h2>
          <button className="btn btn-secondary" onClick={() => navigate(`/project-hub/${projectName}`)}>← חזרה</button>
        </div>

        {/* Controls */}
        <div className="inline-form" style={{ marginBottom: 20 }}>
          <div className="inline-form-row reports-controls-row" style={{ alignItems: 'flex-end', gap: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>סינון לפי שלב</label>
              <div style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: 6, maxHeight: 160, overflowY: 'auto', background: '#fff', direction: 'rtl' }}>
                {stages.map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '2px 4px', whiteSpace: 'nowrap' }}>
                    <input
                      type="checkbox"
                      checked={selectedStages.includes(s)}
                      onChange={() => toggleStage(s)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </div>
            <button className="btn btn-primary" onClick={fetchStats}>הצג</button>
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('export/tenants', `tenants_${decodeURIComponent(projectName)}_${new Date().toISOString().slice(0,10)}.xlsx`)}
            >
              ייצוא פרטי דיירים ⬇
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('export/percentages', `percentages_${decodeURIComponent(projectName)}_${new Date().toISOString().slice(0,10)}.xlsx`)}
            >
              ייצוא אחוזי חתימות ⬇
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('export/contacts', `contacts_${decodeURIComponent(projectName)}_${new Date().toISOString().slice(0,10)}.csv`)}
            >
              ייצוא אנשי קשר לגוגל ⬇
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleExport('export/presentation', `presentation_${decodeURIComponent(projectName)}_${new Date().toISOString().slice(0,10)}.pptx`)}
            >
              ייצוא מצגת ⬇
            </button>
          </div>
        </div>

        {/* Stats table */}
        {loading ? <p>טוען...</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>שלב</th>
                  <th>כתובת</th>
                  <th>מספר בניין</th>
                  <th>גוש</th>
                  <th>חלקה</th>
                  <th>כמות דירות</th>
                  <th>דיור ציבורי</th>
                  <th>כמות חתומים</th>
                  <th>חתומים + דיור ציבורי</th>
                  <th>אחוז</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const pct = parseFloat(row.percentage) || 0;
                  const threshold = row.isTotal ? 66.67 : 60;
                  const pctColor = pct >= threshold ? '#dcfce7' : '#fee2e2';
                  const pctText  = pct >= threshold ? '#166534' : '#991b1b';
                  return (
                    <tr key={i} style={row.isTotal ? { fontWeight: 700, borderTop: '2px solid #0f2d55' } : {}}>
                      <td>{row.stage}</td>
                      <td>{row.buildingAddress}</td>
                      <td>{row.buildingNumber}</td>
                      <td>{row.section}</td>
                      <td>{row.parcel}</td>
                      <td>{row.totalApartments}</td>
                      <td>{row.publicHousingCount}</td>
                      <td>{parseFloat(row.signedPercentage).toFixed(2)}</td>
                      <td>{parseFloat(row.totalSigned).toFixed(2)}</td>
                      <td style={{ background: pctColor, color: pctText, fontWeight: 700 }}>
                        {pct.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
